<script setup lang="ts">
import { kebabCase } from 'scule'
import { joinURL } from 'ufo'
import { SUPPORTED_DOCS_PATH_REGEX } from '#shared/utils/docs'
import type { NavNode } from '~/utils/content'

definePageMeta({
  heroBackground: 'opacity-30',
  key: 'docs'
})

const navigation = inject<Ref<NavNode[]>>('navigation', ref([]))
const menuDrawerOpen = ref(false)
const onThisPageDrawerOpen = ref(false)

const route = useRoute()
const nuxtApp = useNuxtApp()
const { version } = useDocsVersion()
const { headerLinks } = useHeaderLinks()
const { isAgentDocked } = useNuxtAgent()
const path = computed(() => route.path.replace(/\/$/, ''))

const ignoredPaths = ['.nuxt', '.output', '.env', 'node_modules']
const navClass = (item: NavNode) => {
  if (ignoredPaths.includes(String(item.title)) && item.path && !route.path.includes(item.path)) {
    return 'opacity-70 hover:opacity-100'
  }
  return ''
}

const asideNavigation = computed(() => {
  const sectionPath = [version.value.path, route.params.slug?.[version.value.path.split('/').length - 2]].filter(Boolean).join('/')

  const nav = navPageFromPath(sectionPath, navigation.value)?.children || []

  return nav.map(item => ({
    ...item,
    class: navClass(item)
  }))
})

function paintResponse() {
  if (import.meta.server) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    setTimeout(resolve, 100)
    requestAnimationFrame(() => setTimeout(resolve, 0))
  })
}

const pageKey = computed(() => kebabCase(path.value))

const { data: page, status } = await useAsyncData(pageKey, () => {
  const pagePath = path.value
  return paintResponse().then(() => clientContent.get(pagePath))
})

const surround = computed(() => {
  const pages = flattenNavPages(navigation.value)
  const index = pages.findIndex(item => item.path === path.value)
  if (index === -1) {
    return []
  }
  return [pages[index - 1], pages[index + 1]].map(item => item
    ? { title: item.title, path: item.path, description: item.description }
    : undefined
  )
})

watch(status, (status) => {
  if (status === 'pending') {
    nuxtApp.hooks.callHook('page:loading:start')
  } else if (status === 'success' || status === 'error') {
    nuxtApp.hooks.callHook('page:loading:end')
  }
})

watch(route, () => {
  menuDrawerOpen.value = false
  onThisPageDrawerOpen.value = false
})

watch(page, (page) => {
  if (!page) {
    throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
  }
}, { immediate: true })

const currentSectionTitle = computed(() =>
  headerLinks.value[0]?.children?.find(link => path.value.includes(link.to))?.label
  || findPageBreadcrumb(navigation.value, path.value).slice(-1)[0]?.title
  || '')

const breadcrumb = computed(() => {
  const links = findPageBreadcrumb(navigation.value, path.value).map(item => ({
    label: item.title,
    to: item.path
  })).slice(1)

  if (path.value.startsWith(`${version.value.path}/bridge`) || path.value.startsWith(`${version.value.path}/migration`)) {
    links.unshift({
      label: 'Upgrade Guide',
      to: `${version.value.path}/getting-started/upgrade`
    })
  }
  if (!links.length) {
    links.push({
      label: currentSectionTitle.value,
      to: path.value
    })
  }

  return links
})

const editLink = computed(() => {
  if (!page.value) return ''
  const extension = page.value.meta.extension || '.md'
  const stem = page.value.meta.stem.replace(/^4\.examples\//, '')
  if (page.value.meta.source.startsWith('examples')) {
    return `https://github.com/nuxt/examples/edit/main/.docs/${stem}${extension}`
  }
  return `https://github.com/nuxt/nuxt/edit/${version.value.branch}/docs/${stem}${extension}`
})

const communityLinks = [{
  icon: 'i-lucide-heart',
  label: 'Become a Sponsor',
  to: 'https://go.nuxt.com/sponsor',
  target: '_blank'
}, {
  icon: 'i-lucide-chef-hat',
  label: 'Master Nuxt',
  to: 'https://masteringnuxt.com/nuxt3',
  target: '_blank'
}, {
  icon: 'i-lucide-award',
  label: 'Nuxt Certification',
  to: 'https://certification.nuxt.com',
  target: '_blank'
}]

const title = computed(() => page.value?.data.head?.title || page.value?.data.title)
const titleTemplate = computed(() => `${findTitleTemplate(page.value?.path, page.value?.data.titleTemplate, navigation.value, version.value.path)} ${version.value.shortTag}`)

useSeoMeta({
  titleTemplate,
  title
})
if (SUPPORTED_DOCS_PATH_REGEX.test(path.value)) {
  useCanonical(() => `${path.value}.md`)
}

if (import.meta.server) {
  prerenderRoutes([joinURL('/raw', `${path.value}.md`)])

  useSchemaOrg([
    defineArticle({
      '@type': 'TechArticle',
      'headline': page.value?.data.title,
      'description': page.value?.data.head?.description || page.value?.data.description
    }),
    defineBreadcrumb({
      itemListElement: breadcrumb.value.map(item => ({
        name: item.label,
        item: item.to
      }))
    })
  ])

  const description = page.value?.data.head?.description || page.value?.data.description
  useSeoMeta({
    description,
    ogDescription: description,
    ogTitle: titleTemplate.value?.includes('%s') ? titleTemplate.value.replace('%s', title.value) : title.value
  })

  defineOgImage('Docs.takumi', {
    headline: breadcrumb.value.length ? breadcrumb.value.map(link => link.label).join(' > ') : '',
    title,
    description
  })
}

function refreshHeading(opened: boolean) {
  if (!opened) return
  nextTick(() => nuxtApp.callHook('page:loading:end'))
}

const noRightAside = computed(() => route.path.includes('/examples/'))
const feedbackPage = computed(() => ({
  title: page.value?.data.title ?? '',
  stem: page.value?.meta.stem ?? ''
}))
</script>

<template>
  <UContainer v-if="page">
    <DocsVersionAlert />
    <UPage>
      <template #left>
        <UPageAside>
          <UContentNavigation
            :navigation="asideNavigation"
            :collapsible="false"
            highlight
          />
        </UPageAside>
      </template>
      <UPage
        :ui="isAgentDocked || noRightAside? {
          center: 'lg:col-span-10',
          right: 'lg:hidden'
        } : { root: 'lg:grid-cols-12', center: 'lg:col-span-9', right: 'lg:col-span-3' }"
      >
        <UPageHeader
          :ui="{
            wrapper: 'flex-row items-center flex-wrap justify-between'
          }"
          :title="page.data.title"
          :description="page.data.description"
        >
          <template #headline>
            <UBreadcrumb :items="breadcrumb" />
          </template>

          <template #title>
            {{ page.data.title }}

            <UBadge
              v-if="page.data.minimalVersion?.trim()"
              :label="`v${page.data.minimalVersion?.trim()}`"
              color="info"
              variant="subtle"
              size="lg"
              class="align-middle"
              :aria-label="`Minimum Nuxt version: v${page.data.minimalVersion?.trim()}`"
            />
          </template>

          <template #links>
            <UButton
              v-for="link in page.data.links?.map(link => ({ ...link, size: 'md' }))"
              :key="link.label"
              color="neutral"
              variant="soft"
              :target="link.to.startsWith('http') ? '_blank' : undefined"
              v-bind="{ ...link, size: 'sm' }"
            >
              <template v-if="link.avatar" #leading>
                <UAvatar v-bind="link.avatar" size="2xs" :alt="`${link.label} avatar`" />
              </template>
            </UButton>
            <PageHeaderLinks :key="page.path" />
          </template>
        </UPageHeader>

        <UPageBody>
          <MarkdownDocument v-if="page.nodes?.length" :value="page" />
          <div>
            <Feedback :page="feedbackPage" />
            <USeparator class="mt-6 mb-10">
              <div class="flex items-center gap-2 text-sm text-muted">
                <UButton
                  size="sm"
                  variant="link"
                  color="neutral"
                  to="https://github.com/nuxt/nuxt/issues/new/choose"
                  target="_blank"
                  label="Report an issue"
                />
                or
                <UButton
                  size="sm"
                  variant="link"
                  color="neutral"
                  :to="editLink"
                  target="_blank"
                  label="Edit this page on GitHub"
                />
              </div>
            </USeparator>
            <UContentSurround :surround="surround" />
          </div>
        </UPageBody>

        <template #right>
          <ContentToc
            v-if="!isAgentDocked && !noRightAside"
            :links="page.meta?.toc?.links"
            :community-links="communityLinks"
            highlight
            highlight-variant="circuit"
            class="hidden lg:flex lg:backdrop-blur-none lg:overflow-y-auto"
            :ui="{
              container: 'lg:max-h-[inherit]',
              content: 'lg:min-h-[min(var(--list-height,8rem),8rem)]'
            }"
          />
          <!-- mobile -->
          <div class="order-first lg:order-last sticky top-(--ui-header-height) z-10 bg-default/75 lg:bg-[initial] backdrop-blur -mx-4 p-6 border-b border-dashed border-default flex justify-between">
            <UDrawer
              v-model:open="menuDrawerOpen"
              direction="left"
              :title="currentSectionTitle"
              inset
              :handle="false"
              side="left"
              class="lg:hidden"
              :ui="{
                content: 'w-full max-w-2/3'
              }"
            >
              <UButton
                label="Menu"
                icon="i-lucide-text-align-start"
                color="neutral"
                variant="link"
                size="xs"
                aria-label="Open navigation"
                class="-m-4"
              />
              <template #body>
                <UContentNavigation
                  :navigation="asideNavigation"
                  default-open
                  trailing-icon="i-lucide-chevron-right"
                  :ui="{ linkTrailingIcon: 'group-data-[state=open]:rotate-90' }"
                  highlight
                />
              </template>
            </UDrawer>
            <UDrawer
              v-if="!noRightAside"
              v-model:open="onThisPageDrawerOpen"
              direction="right"
              :handle="false"
              side="right"
              inset
              class="lg:hidden"
              no-body-styles
              :ui="{
                content: 'w-full max-w-2/3'
              }"
              @update:open="refreshHeading"
            >
              <UButton
                label="On this page"
                trailing-icon="i-lucide-chevron-right"
                color="neutral"
                variant="link"
                size="xs"
                aria-label="Open on this page"
                class="-m-4"
              />
              <template #body>
                <ContentToc
                  :links="page.meta?.toc?.links"
                  :community-links="communityLinks"
                  :open="true"
                  default-open
                  :ui="{
                    root: '!mx-0 !px-1 top-0 overflow-visible',
                    container: '!pt-0 border-b-0',
                    trailingIcon: 'hidden',
                    bottom: 'flex flex-col'
                  }"
                />
              </template>
            </UDrawer>
          </div>
        </template>
      </UPage>
    </UPage>
  </UContainer>
</template>
